import Quill from 'quill'
import scrollIntoView from 'scroll-into-view-if-needed'
import { lastIndexOfRegex } from './utils'
import { addEscapeListener, removeEscapeListener } from '@/helpers/escape'
import {
	searchEmojisByShortcode,
	findEmojiExactMatch,
	getPreferredSkinToneForEmoji,
} from '@/helpers/emojis'
import { trackEvent } from '@/helpers/utils'

const Module = Quill.import('core/module')

const EMOJI_START_REGEX = /((^|\s|\n):)/g
const MAX_RESULTS = 10

const removeLastChar = (text) => text.slice(0, text.length - 1)

class ShortNameEmoji extends Module {
	constructor(quill, options) {
		super(quill, options)

		this.quill = quill
		this.container = document.createElement('div')
		this.container.classList.add('emoji_completions')
		this.quill.container.appendChild(this.container)

		Object.assign(this, {
			open: false,
			focusedButton: null,
			onSuggestionsChange: options.onSuggestionsChange,
		})

		const bindings = [
			{ key: 'ArrowLeft', handler: this.moveToPrev.bind(this) }, // ArrowLeft
			{ key: 'ArrowUp', handler: this.moveToPrev.bind(this) }, // ArrowUp
			{ key: 'ArrowRight', handler: this.moveToNext.bind(this) }, // ArrowRight
			{ key: 'ArrowDown', handler: this.moveToNext.bind(this) }, // ArrowDown
		]

		bindings.forEach(({ handler, ...info }) => {
			quill.keyboard.addBinding(info, { collapsed: true }, handler)
		})

		// https://stackoverflow.com/a/63803445/1313904
		// no idea why I have to register this 'Enter' handler differently,
		// but this is the only way it seems to have worked
		quill.keyboard.bindings['Enter'].unshift({
			key: 'Enter',
			collapsed: true,
			handler: this.selectEmoji.bind(this),
		})

		this.addDOMEvent(document.body, 'click', (e) => {
			if (!this.open || this.container.contains(e.target)) return
			this.hideSuggestions()
		})

		this.addQuillEvent('editor-change', () => this.onTextChange())
	}

	destroy() {
		this.hideSuggestions()
		this.removeEventBusEvents?.()
		this.quillEvents?.forEach((e) => this.quill.off(e.event, e.handler))
		this.domEvents?.forEach((e) =>
			e.element.removeEventListener(e.event, e.handler)
		)
	}

	addQuillEvent(event, handler) {
		this.quill.on(event, handler)
		this.quillEvents = this.quillEvents || []
		this.quillEvents.push({ event, handler })
	}

	addDOMEvent(element, event, handler) {
		element.addEventListener(event, handler)
		this.domEvents = this.domEvents || []
		this.domEvents.push({ element, event, handler })
	}

	onTextChange() {
		const selection = this.quill.getSelection()
		if (!selection) return true
		const { index: caret } = selection
		const fullText = this.quill.getText()
		if (!fullText.match(/(^|\s):\S/g) || fullText.endsWith('\n\n')) {
			// no colon or user has just entered a new line, so close
			return this.close(null)
		}

		const { ops } = this.quill.getContents(0, caret)
		const mentions = ops.filter((op) => op.insert?.mention)

		// get piece of text from start of message up to the current caret position
		let text = fullText.slice(0, caret - mentions.length)
		if (!text.includes(':')) {
			// current section of text doesn't include a ':' character
			return this.close(null)
		}

		// for some reason, quill adds a new line car whenever
		// the user enters multiple emojis in a single message
		if (text.endsWith('\n')) {
			// if this is case, remove it to get an accurate query
			text = removeLastChar(text)
		}

		// find index of last colon in message
		let colon = lastIndexOfRegex(text, EMOJI_START_REGEX)

		if (text.endsWith(':')) {
			// if text ends with ':', adjust colon to allow for an exact match
			colon = lastIndexOfRegex(removeLastChar(text), EMOJI_START_REGEX)
		}

		// find query text from last ':' up to the current caret position
		let query = (this.query = text.slice(colon, text.length).trimStart())

		if (/\s/.test(query)) {
			// query contains whitespace, so stop matching and bail
			return this.close(null)
		}

		// trim the leading ':' to perform emoji search
		const withoutColon = query.slice(1)

		if (withoutColon.endsWith(':')) {
			// look for exact match
			const emojiQuery = removeLastChar(withoutColon)
			const emoji = findEmojiExactMatch(emojiQuery)
			if (emoji) {
				trackEvent('emoji_shortcode_exact_match', {
					query: emojiQuery,
					emoji: emoji.emoji,
					label: emoji.label,
				})
				this.close(emoji)
				return false
			}
		}

		const emojis = searchEmojisByShortcode(query, MAX_RESULTS) || []

		if (emojis?.length) {
			this.open = true
			this.renderCompletions(emojis)
			return false
		}
		this.close(null)
	}

	selectEmoji() {
		if (!this.open) return true
		const button = this.getSelectedButton()
		const emojiStr = button.dataset.emoji
		if (!emojiStr) return true
		this.close(emojiStr)
	}

	moveToNext() {
		if (!this.open) return true
		const current = this.getSelectedButton() || {}
		this.highlight(current.nextSibling || this.firstEmoji)
	}

	moveToPrev() {
		if (!this.open) return true
		const current = this.getSelectedButton() || {}
		this.highlight(current.previousSibling || this.lastEmoji)
	}

	renderCompletions(emojis) {
		this.clear()

		const buttons = emojis.map(({ emoji, label }) => {
			const prefSkin = getPreferredSkinToneForEmoji(emoji)
			const emojiToDisplay = prefSkin?.emoji || emoji
			const button = document.createElement('button')
			button.className = 'emoji-suggestion'
			button.dataset.emoji = emojiToDisplay
			button.innerHTML = `${emojiToDisplay} <span class="emoji-name">${label}</span>`
			this.container.appendChild(button)
			this.addSuggestionEvent(button, 'click', () => this.close(emoji))
			this.addSuggestionEvent(button, 'mouseover', () => this.highlight(button))
			return button
		})

		this.firstEmoji = buttons[0]
		this.lastEmoji = buttons.slice(-1)[0]

		this.container.style.display = 'flex'
		this.highlight(this.firstEmoji)
		this.onSuggestionsChange(true, 'emoji')
		if (!this.escapeId) {
			this.escapeId = addEscapeListener(() => {
				this.hideSuggestions()
			})
		}
	}

	addSuggestionEvent(item, event, handler) {
		item.addEventListener(event, handler)
		this.suggestionEvents = this.suggestionEvents || []
		this.suggestionEvents.push({ item, event, handler })
	}

	getSelectedButton() {
		return this.container.querySelector('.emoji-active')
	}

	highlight(button) {
		const old = this.getSelectedButton()
		if (old) {
			old.classList.remove('emoji-active')
		}
		button.classList.add('emoji-active')
		scrollIntoView(button, {
			scrollMode: 'if-needed',
			block: 'nearest',
			inline: 'nearest',
		})
	}

	clear() {
		while (this.container.firstChild) {
			this.container.removeChild(this.container.firstChild)
		}
	}

	hideSuggestions() {
		this.suggestionEvents?.forEach((e) =>
			e.item.removeEventListener(e.event, e.handler)
		)
		this.suggestions = []
		this.suggestionEvents = []
		this.container.style.display = 'none'
		this.open = false
		this.onSuggestionsChange(false, 'emoji')
		removeEscapeListener(this.escapeId)
		this.escapeId = null
	}

	insertEmoji(emojiStr) {
		if (!emojiStr) return
		const selection = this.quill.getSelection(true)
		const queryLength = this.query.length
		const index = selection.index - queryLength
		this.quill.deleteText(index, queryLength, Quill.sources.USER)
		this.quill.insertText(index, emojiStr, Quill.sources.USER)
		this.quill.insertText(index + emojiStr.length, ' ', Quill.sources.USER)
		setTimeout(() => {
			const sel = this.quill.getSelection()
			const pos = sel.index + emojiStr.length + 1
			this.quill.setSelection(pos, 0, Quill.sources.USER)
		})
	}

	close(emojiString) {
		this.clear()
		if (emojiString) {
			this.insertEmoji(emojiString)
		}
		this.quill.focus()
		this.hideSuggestions()
	}
}

export default ShortNameEmoji
